// /profile — the editorial "practice journal" view. Composes the data
// /users/me already returns (xp, currentStreak, longestStreak,
// lastPracticeDate, completedObjectives) and runs it through the level
// math from lib/levels.ts. Read-only for M2.4a; M2.4b adds weekly streak
// progress; M2.4c adds the recap card.

import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import WeeklyRecapBlock from "../components/WeeklyRecap";
import { formatXp, levelFromXp, levelRoman } from "../lib/levels";
import { useCurrentUser } from "../lib/use-current-user";

// Monday-first week display, to match the API's ISO-week aggregation.
const PRACTICE_DAY_INITIALS = ["M", "T", "W", "T", "F", "S", "S"] as const;

function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

// Monday of the ISO week containing dateKey. TZ-naive (operates on the
// calendar date already in PRACTICE_TIME_ZONE form).
function mondayOf(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // Sun=0, Mon=1, ... Sat=6
  const offset = dow === 0 ? 6 : dow - 1;
  return addDays(dateKey, -offset);
}

function formatLastPractice(dateKey: string | null): string {
  if (!dateKey) return "no practice on record yet";
  // dateKey is YYYY-MM-DD in America/Denver — parse as a local-noon date so
  // toLocaleDateString never lands on the wrong day from TZ math.
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return "no practice on record yet";
  const noon = new Date(Date.UTC(y, m - 1, d, 18, 0)); // 18 UTC ≈ noon MT
  const fmt = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(noon);
  return `last practiced ${fmt}`;
}

function ProfileShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      <div className="mx-auto max-w-2xl px-6 pt-10 pb-24 sm:pt-16">
        <header className="flex items-baseline justify-between text-sm">
          <Link
            to="/library"
            className="text-gray-400 hover:text-orange-300 transition tracking-wide"
          >
            ← Library
          </Link>
          <span className="font-display text-gray-500 text-xs tracking-[0.3em] uppercase">
            Practice Journal
          </span>
        </header>
        {children}
      </div>
    </div>
  );
}

function PracticeWeekDots({
  weekStart,
  practiceDays,
  todayKey,
}: {
  weekStart: string | null;
  practiceDays: readonly string[];
  todayKey: string;
}) {
  // Render the seven days of the current ISO week as a Mon→Sun grid.
  // Filled dot = practiced. Today gets a halo regardless.
  const start = weekStart ?? todayKey;
  const practicedSet = new Set(practiceDays);
  return (
    <div className="mt-2 flex items-end justify-center gap-4 font-display text-gray-500 text-xs tracking-[0.3em]">
      {PRACTICE_DAY_INITIALS.map((letter, i) => {
        const key = addDays(start, i);
        const practiced = practicedSet.has(key);
        const isToday = key === todayKey;
        return (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <span
              className={`h-2 w-2 rounded-full transition ${
                practiced
                  ? "bg-orange-300 ring-1 ring-orange-300/40 ring-offset-2 ring-offset-gray-950"
                  : isToday
                    ? "bg-gray-700 ring-1 ring-gray-600/60 ring-offset-2 ring-offset-gray-950"
                    : "bg-gray-800"
              }`}
              aria-label={
                practiced ? `practiced ${key}` : `no practice ${key}`
              }
            />
            <span
              className={
                practiced
                  ? "text-orange-200"
                  : isToday
                    ? "text-gray-400"
                    : ""
              }
            >
              {letter}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatLine({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3">
      <span className="font-display text-gray-500 text-xs tracking-[0.22em] uppercase">
        {label}
      </span>
      <span className="text-right">
        <span className="font-display text-2xl text-gray-100 tabular-nums">
          {value}
        </span>
        {detail ? (
          <span className="ml-2 font-display-italic text-sm text-gray-500">
            {detail}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export default function Profile() {
  const { user, signOut } = useAuth();
  const { profile, loading, error } = useCurrentUser();

  if (loading && !profile) {
    return (
      <ProfileShell>
        <div className="mt-24 text-center font-display-italic text-gray-500">
          gathering this season&rsquo;s work…
        </div>
      </ProfileShell>
    );
  }

  if (error || !profile) {
    return (
      <ProfileShell>
        <div className="mt-24 text-center">
          <p className="font-display text-xl text-gray-300">
            We couldn&rsquo;t load your profile.
          </p>
          {error ? (
            <p className="mt-2 font-display-italic text-sm text-gray-500">
              {error}
            </p>
          ) : null}
        </div>
      </ProfileShell>
    );
  }

  const { stats } = profile;
  const lvl = levelFromXp(stats.xp);
  const displayName =
    profile.user.displayName ??
    profile.user.givenName ??
    user?.givenName ??
    "friend";

  const todayKey = (() => {
    const now = new Date();
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: stats.practiceTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  })();
  const practicedToday = stats.lastPracticeDate === todayKey;
  // Fall back to the Monday of today's local week when the backend hasn't
  // supplied it (mock-auth mode, or a brand-new user pre-first-practice).
  const weekStart = stats.currentWeekStartDate ?? mondayOf(todayKey);
  const weekDays = stats.currentWeekPracticeDays ?? [];
  const weekTarget = stats.weekTarget ?? 4;
  const weekDaysCount = weekDays.length;
  const weeklyStreak = stats.weeklyStreak ?? 0;
  const longestWeeklyStreak = stats.longestWeeklyStreak ?? 0;
  const breakdown = stats.xpBreakdown;

  return (
    <ProfileShell>
      {/* Title block — handwritten-journal hierarchy. The name is the cover, */}
      {/* the date underneath is the entry header. */}
      <section className="mt-12 text-center">
        <p className="font-display-italic text-gray-500 text-sm">
          for
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-orange-100 tracking-tight">
          {displayName}
        </h1>
        <p className="mt-2 font-display-italic text-gray-500 text-sm">
          {formatLastPractice(stats.lastPracticeDate)}
        </p>
      </section>

      {/* Big XP — the single hero element on the page. */}
      <section className="mt-16 text-center">
        <p className="font-display text-gray-500 text-[10px] tracking-[0.35em] uppercase">
          experience
        </p>
        <div className="mt-3 font-display text-7xl sm:text-8xl text-white tabular-nums leading-none">
          {formatXp(stats.xp)}
        </div>
        <p className="mt-2 font-display-italic text-gray-400 text-base">
          Level&nbsp;{levelRoman(lvl.level)}&nbsp;·&nbsp;{lvl.name}
        </p>
        {lvl.xpToNext > 0 ? (
          <p className="mt-1 text-xs text-gray-500 font-display">
            {formatXp(lvl.xpToNext)} to {levelRoman(lvl.level + 1)}
          </p>
        ) : null}

        {/* Progress bar — hairline, restrained. */}
        <div className="mx-auto mt-5 h-px max-w-xs bg-gray-800/80 relative">
          <div
            className="absolute inset-y-0 left-0 bg-orange-300/70"
            style={{
              width: `${Math.max(2, Math.min(100, lvl.progress * 100))}%`,
              height: "1px",
              boxShadow: "0 0 8px rgba(253, 186, 116, 0.5)",
            }}
            aria-hidden
          />
        </div>
      </section>

      <hr className="journal-rule mx-auto my-12 max-w-md" />

      {/* M2.4c — weekly recap (editorial composition). */}
      {stats.weeklyRecap ? (
        <>
          <WeeklyRecapBlock
            recap={stats.weeklyRecap}
            weekStart={weekStart}
            weekTarget={weekTarget}
            practiceDaysThisWeek={weekDaysCount}
            todayKey={todayKey}
          />
          <hr className="journal-rule mx-auto my-12 max-w-md" />
        </>
      ) : null}

      {/* This week + streak — composed as a journal entry. */}
      <section className="text-center">
        <p className="font-display text-gray-500 text-[10px] tracking-[0.35em] uppercase">
          this week
        </p>
        <PracticeWeekDots
          weekStart={weekStart}
          practiceDays={weekDays}
          todayKey={todayKey}
        />{/* weekStart is always a real Monday now (computed if backend omits) */}
        <p className="mt-6 font-display-italic text-gray-300">
          {weekDaysCount === 0
            ? practicedToday
              ? "today on the board"
              : "a fresh week"
            : weekDaysCount >= weekTarget
              ? `${weekDaysCount} days this week — target met`
              : `${weekDaysCount} of ${weekTarget} days this week`}
        </p>
        {weeklyStreak > 0 ? (
          <p className="mt-2 font-display-italic text-orange-200/90 text-sm">
            {weeklyStreak === 1
              ? "one week running"
              : `${weeklyStreak} weeks running`}
          </p>
        ) : null}
        {longestWeeklyStreak > weeklyStreak ? (
          <p className="mt-1 text-xs text-gray-500 font-display">
            longest run of weeks: {longestWeeklyStreak}
          </p>
        ) : null}
        {stats.longestStreak > stats.currentStreak ? (
          <p className="mt-1 text-xs text-gray-500 font-display">
            longest daily streak: {stats.longestStreak}{" "}
            {stats.longestStreak === 1 ? "day" : "days"}
          </p>
        ) : null}
      </section>

      <hr className="journal-rule mx-auto my-12 max-w-md" />

      {/* Sub-stats list — small, calm, no flourishes. */}
      <section className="mx-auto max-w-md">
        <p className="text-center font-display text-gray-500 text-[10px] tracking-[0.35em] uppercase mb-4">
          ledger
        </p>
        <div className="divide-y divide-gray-800/60 border-y border-gray-800/60">
          <StatLine
            label="Objectives checked"
            value={formatXp(stats.completedObjectives)}
            detail={`+${formatXp(breakdown?.objectives ?? stats.completedObjectives * stats.xpPerObjective)} XP`}
          />
          {(stats.completedVideos ?? 0) > 0 || (breakdown?.videosComplete ?? 0) > 0 ? (
            <StatLine
              label="Videos finished"
              value={formatXp(stats.completedVideos ?? 0)}
              detail={`+${formatXp(breakdown?.videosComplete ?? 0)} XP`}
            />
          ) : null}
          {(breakdown?.firstOfDayBonuses ?? 0) > 0 ? (
            <StatLine
              label="First-of-day bonuses"
              value={formatXp(
                (breakdown!.firstOfDayBonuses ?? 0) /
                  (stats.xpPerFirstActionOfDay ?? 20),
              )}
              detail={`+${formatXp(breakdown!.firstOfDayBonuses)} XP`}
            />
          ) : null}
          {(breakdown?.bookmarks ?? 0) > 0 ? (
            <StatLine
              label="Bookmarks (capped)"
              value={formatXp(
                (breakdown!.bookmarks ?? 0) / (stats.xpPerBookmark ?? 2),
              )}
              detail={`+${formatXp(breakdown!.bookmarks)} XP`}
            />
          ) : null}
          <StatLine
            label="Longest daily streak"
            value={`${stats.longestStreak}`}
            detail={stats.longestStreak === 1 ? "day" : "days"}
          />
          {(stats.longestWeeklyStreak ?? 0) > 0 ? (
            <StatLine
              label="Longest weekly run"
              value={`${stats.longestWeeklyStreak ?? 0}`}
              detail={(stats.longestWeeklyStreak ?? 0) === 1 ? "week" : "weeks"}
            />
          ) : null}
          <StatLine
            label="Practice time zone"
            value={stats.practiceTimeZone.replace("_", " ")}
          />
        </div>
      </section>

      <footer className="mt-16 text-center">
        <p className="font-display-italic text-gray-600 text-xs mb-3">
          signed in as {profile.user.email ?? "—"}
        </p>
        <button
          type="button"
          onClick={signOut}
          className="font-display text-xs tracking-[0.3em] uppercase text-gray-500 hover:text-orange-300 transition"
        >
          Sign out
        </button>
      </footer>
    </ProfileShell>
  );
}
